/**
 * Returns an inline address from an Address object.
 *
 * @param {Object} addressObject - The input object containing address details. ie:
 *          {
                "coordinates": {
                    "lat": -33.8349807,
                    "lng": 151.0514108
                },
                "name": null,
                "postcode": "2128",
                "state": "NSW",
                "street": "19-23 Fariola Street",
                "suburb": "SILVERWATER"
            }

 * @returns {string} The formatted address string. ie:
            19-23 Fariola Street, SILVERWATER, NSW
 */
export function inlineAddress(addressObject) {
    const { street, suburb, state, postcode } = addressObject || {};
    // Create an array of address components
    const addressComponents = [street, suburb, state, postcode];
    // Filter out null and empty components
    const nonEmptyComponents = addressComponents.filter(component => component !== null && component !== undefined && component !== "");
    // Join the non-empty components with commas
    const formattedAddress = nonEmptyComponents.join(", ");
    return formattedAddress;
}

export const STREET_TYPES = [
  "street", "st", "road", "rd", "avenue", "ave", "drive", "dr",
  "lane", "ln", "place", "pl", "way", "boulevard", "blvd",
  "parade", "pde", "court", "ct", "circuit", "cct", "highway", "hwy",
  "crescent", "cres", "close", "cl", "terrace", "tce", "track", "trk",
  "trail", "walk", "loop", "grove", "gr", "rise", "crest", "row",
  "mews", "esplanade", "esp", "freeway", "fwy", "motorway", "mwy",
  "parkway", "pkwy", "passage", "pass", "alley", "mall"
];

const streetTypeRegex = new RegExp("\\b(" + STREET_TYPES.join("|") + ")\\b", "i");

/**
 * Validates that an address is a full address (MUST have street and suburb; street number is optional).
 *
 * @param {string|object} addr - Address string or object with street/suburb
 * @returns {{ valid: boolean, reason?: string, street?: string, suburb?: string }}
 */
export function validateFullAddress(addr) {
  if (!addr) {
    return { valid: false, reason: "Address is required" };
  }

  // If passed an address object
  if (typeof addr === "object" && addr !== null) {
    const st = addr.street || addr.address || "";
    const sub = addr.suburb || "";
    if (!st || !st.trim()) {
      return { valid: false, reason: "Street is required" };
    }
    if (!sub || !sub.trim()) {
      return validateFullAddress(st);
    }
    const hasStreet = streetTypeRegex.test(st);
    if (!hasStreet && st.trim().toLowerCase() === sub.trim().toLowerCase()) {
      return { valid: false, reason: "Street name is required (suburb only provided)" };
    }
    return { valid: true, street: st.trim(), suburb: sub.trim() };
  }

  if (typeof addr !== "string") {
    return { valid: false, reason: "Invalid address format" };
  }

  const clean = addr.trim();
  if (clean.length < 5) {
    return { valid: false, reason: "Address is too short" };
  }

  const hasStreetType = streetTypeRegex.test(clean);

  // Comma-separated format
  const parts = clean.split(",").map(p => p.trim()).filter(Boolean);

  if (parts.length >= 2) {
    const firstPartHasStreetType = streetTypeRegex.test(parts[0]);
    const secondPart = parts[1];
    const isStateOnly = /^(NSW|VIC|QLD|WA|SA|TAS|ACT|NT|New South Wales|Victoria|Queensland|Western Australia|South Australia|Tasmania|Australia)$/i.test(secondPart);

    if (firstPartHasStreetType && !isStateOnly) {
      return { valid: true, street: parts[0], suburb: parts[1] };
    }
    if (firstPartHasStreetType && isStateOnly && parts.length >= 3) {
      return { valid: true, street: parts[0], suburb: parts[2] };
    }
    if (!firstPartHasStreetType && hasStreetType) {
      return { valid: true, street: parts.slice(0, -1).join(", "), suburb: parts[parts.length - 1] };
    }
    if (!hasStreetType) {
      return { valid: false, reason: "Street name is required. Must have street and suburb (street number is optional)." };
    }
  } else {
    // Single line without commas e.g. "123 Church St Parramatta" or "Church St Parramatta"
    if (!hasStreetType) {
      return { valid: false, reason: "Street name is required. Must have street and suburb (street number is optional)." };
    }
    const match = clean.match(new RegExp("^(.*?\\b(?:" + STREET_TYPES.join("|") + ")\\b)(.*)$", "i"));
    if (match && match[2] && match[2].trim().length > 1) {
      return { valid: true, street: match[1].trim(), suburb: match[2].trim() };
    } else {
      return { valid: false, reason: "Suburb is required. Must have street and suburb." };
    }
  }

  return { valid: hasStreetType, reason: hasStreetType ? "" : "Street name is required." };
}

/**
 * Returns true if the address has both a street and a suburb (street number is optional).
 *
 * @param {string|object} addr
 * @returns {boolean}
 */
export function isFullAddress(addr) {
  return validateFullAddress(addr).valid;
}

export function parseMapboxAddress(mapboxAddress) {
  if (!mapboxAddress) return null;
  const coordinates = {
    lat: mapboxAddress.center ? mapboxAddress.center[1] : 0,
    lng: mapboxAddress.center ? mapboxAddress.center[0] : 0,
  };

  const postcode = mapboxAddress.context?.find(context => context.id.startsWith('postcode'))?.text || '';
  const state = mapboxAddress.context?.find(context => context.id.startsWith('region'))?.text || '';
  
  let street = '';
  if (mapboxAddress.place_type?.includes('address')) {
    street = mapboxAddress.address ? `${mapboxAddress.address} ${mapboxAddress.text}` : mapboxAddress.text;
  } else {
    street = mapboxAddress.place_name?.split(', ')[0] || '';
  }

  const suburb = mapboxAddress.context?.find(context => context.id.startsWith('locality'))?.text || 
                 mapboxAddress.context?.find(context => context.id.startsWith('place'))?.text || '';

  const validation = validateFullAddress({ street, suburb });

  return {
    coordinates,
    name: null,
    postcode,
    state,
    street,
    suburb,
    place_name: mapboxAddress.place_name || '',
    is_full_address: Boolean(mapboxAddress.place_type?.includes('address') || validation.valid),
  };
}

/**
 * Calculates the time difference between a given date and the current date and returns
 * a human-readable string indicating how long ago the date was.
 * @param {string} dateString - The date in "yyyy-mm-dd" format.
 * @returns {string} A string indicating the time difference, e.g., "today," "1 day," "2 weeks," "> 3 months," etc.
 */
export function timeAgo(dateString) {
    const inputDate = new Date(dateString);
    const currentDate = new Date();
  
    // Check if the input date is today
    if (
      inputDate.getFullYear() === currentDate.getFullYear() &&
      inputDate.getMonth() === currentDate.getMonth() &&
      inputDate.getDate() === currentDate.getDate()
    ) {
      return 'today';
    }
  
    // Adjust the time to the same time of day to ensure accurate date comparison
    currentDate.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);
  
    const timeDifference = currentDate - inputDate;
    const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const weeksDifference = Math.floor(daysDifference / 7);
    const monthsDifference = Math.floor(daysDifference / 30);
  
    if (monthsDifference > 3) {
      return '> 3 months';
    } else if (monthsDifference === 3) {
      return '3 months';
    } else if (monthsDifference === 2) {
      return '2 months';
    } else if (monthsDifference === 1) {
      return '1 month';
    } else if (weeksDifference > 0) {
      return `${weeksDifference} ${weeksDifference === 1 ? 'week' : 'weeks'}`;
    } else {
      return `${daysDifference} ${daysDifference === 1 ? 'day' : 'days'}`;
    }
  }

  // formats the passed in date object to YYYY-MM-DDThh:mm
  export function formatDateTime(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
  
    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
    return formattedDate;
  }

export function formatQuantity(quantity, unit) {
    // Check if quantity is a valid number
    if (!isNaN(quantity) && quantity !== '') {
        quantity = 'up to ' + quantity;
        // Check if unit is null or empty string
        if (unit === null || unit === '') {
            unit = '';
        } else {
            unit += 's';
        }
        return `${quantity} ${unit}`;
    } 
    return '';    
}

export function formatPrice(price, unit) {
    // Check if price is null or a string
    price = parseFloat(price)
    if (price === null || isNaN(price)) {
        return '';
    }

    // Add '$' to the front of the price
    price = '$' + price;

    // Add 'p/' + the first letter in unit to the back
    if (unit) {
        price += 'p/' + unit.charAt(0);
    }

return price;
}

/**
 * Action to portal a DOM node directly to document.body (or a specified target).
 * Useful for modal dialogs to prevent being trapped in nested stacking contexts,
 * overflow containers, or transformed parents.
 */
export function portal(node, target = document.body) {
    target.appendChild(node);
    return {
        destroy() {
            if (node.parentNode) {
                node.parentNode.removeChild(node);
            }
        }
    };
}

/**
 * Set to track already reported yard image errors to prevent redundant duplicate console logs.
 */
const loggedYardImageErrors = new Set();

/**
 * Resolves the display name for a yard across both embedded and reference company formats.
 *
 * @param {Object} yard - The yard data object
 * @returns {string} The resolved yard or company name
 */
export function getYardName(yard) {
    if (!yard) return "Yard";
    if (yard.company && typeof yard.company === "object" && yard.company.name) {
        return yard.company.name;
    }
    if (yard.name) {
        return yard.name;
    }
    if (yard.id !== undefined && yard.id !== null) {
        return `Yard #${yard.id}`;
    }
    return "Yard";
}

/**
 * Logs a console error for any yard with a missing or unfound image.
 *
 * @param {Object} yard - The yard data object
 * @param {"empty"|"not_found"} reason - Whether the image property is empty or the file was not found
 * @param {string} [imagePath] - The image path that failed to load (if applicable)
 */
export function reportYardImageError(yard, reason, imagePath = "") {
    if (!yard) return;
    const yardName = getYardName(yard);
    const yardId = yard.id !== undefined ? yard.id : 'unknown';
    const errorKey = `${yardId}:${reason}:${imagePath}`;

    if (loggedYardImageErrors.has(errorKey)) return;
    loggedYardImageErrors.add(errorKey);

    if (reason === "empty") {
        console.error(`[TruckTrack] Yard missing image: yard.image is empty for "${yardName}" (ID: ${yardId})`);
    } else if (reason === "not_found") {
        console.error(`[TruckTrack] Yard image not found: unable to load image "${yard.image}" for "${yardName}" (ID: ${yardId}) at path "${imagePath}"`);
    }
}

/**
 * Validates a yard's image: logs a console error if yard.image is empty or cannot be found.
 *
 * @param {Object} yard - The yard data object
 */
export function validateYardImage(yard) {
    if (!yard) return;
    if (!yard.image || typeof yard.image !== "string" || yard.image.trim() === "") {
        reportYardImageError(yard, "empty");
        return;
    }

    if (typeof Image !== "undefined") {
        const imagePath = "./assests/yards/" + yard.image;
        const testImg = new Image();
        testImg.onerror = () => {
            reportYardImageError(yard, "not_found", imagePath);
        };
        testImg.src = imagePath;
    }
}

/**
 * Validates an array of yards, logging console errors for any yards with missing or unfound images.
 *
 * @param {Array<Object>} yardsList - List of yard objects
 */
export function validateYardImages(yardsList) {
    if (!Array.isArray(yardsList)) return;
    yardsList.forEach((yard) => validateYardImage(yard));
}

/**
 * Encodes an array of geographic coordinates into a standard Google Maps Encoded Polyline string.
 * Supports GeoJSON coordinates [lng, lat] or objects {lat, lng} / {latitude, longitude}.
 *
 * @param {Array<[number, number]|{lat: number, lng: number}>} coordinates - List of coordinate points.
 * @returns {string} Standard Google encoded polyline string.
 */
export function encodePolyline(coordinates) {
    if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) return "";
    let output = "";
    let prevLat = 0;
    let prevLng = 0;

    function encodeSignedNumber(num) {
        let sgn = num < 0 ? ~(num << 1) : (num << 1);
        let s = "";
        while (sgn >= 0x20) {
            s += String.fromCharCode((0x20 | (sgn & 0x1f)) + 63);
            sgn >>= 5;
        }
        s += String.fromCharCode(sgn + 63);
        return s;
    }

    for (const point of coordinates) {
        let lat = 0;
        let lng = 0;
        if (Array.isArray(point)) {
            // GeoJSON coordinates format: [longitude, latitude]
            lng = Number(point[0]) || 0;
            lat = Number(point[1]) || 0;
        } else if (point && typeof point === "object") {
            lat = Number(point.lat ?? point.latitude) || 0;
            lng = Number(point.lng ?? point.longitude) || 0;
        }

        const late5 = Math.round(lat * 1e5);
        const lnge5 = Math.round(lng * 1e5);

        const dLat = late5 - prevLat;
        const dLng = lnge5 - prevLng;

        prevLat = late5;
        prevLng = lnge5;

        output += encodeSignedNumber(dLat) + encodeSignedNumber(dLng);
    }

    return output;
}




/**
 * Decodes a Google Maps / Mapbox encoded polyline string into an array of GeoJSON [longitude, latitude] coordinates.
 * Supports string encoded polylines, GeoJSON objects, or pre-decoded coordinate arrays.
 *
 * @param {string|Array|Object} encoded - The encoded polyline string or coordinate object.
 * @returns {Array<[number, number]>} Array of [longitude, latitude] coordinate pairs.
 */
export function decodePolyline(encoded) {
    if (!encoded) return [];
    if (Array.isArray(encoded)) {
        return encoded.map(pt => {
            if (Array.isArray(pt)) return [Number(pt[0]), Number(pt[1])];
            if (pt && typeof pt === 'object') return [Number(pt.lng ?? pt.longitude ?? 0), Number(pt.lat ?? pt.latitude ?? 0)];
            return [0, 0];
        });
    }
    if (typeof encoded === 'object' && Array.isArray(encoded.coordinates)) {
        return decodePolyline(encoded.coordinates);
    }
    if (typeof encoded !== 'string') return [];

    const str = encoded.trim();
    if (!str) return [];

    let points = [];
    let index = 0, len = str.length;
    let lat = 0, lng = 0;

    while (index < len) {
        let b, shift = 0, result = 0;
        do {
            b = str.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lat += dlat;

        shift = 0;
        result = 0;
        do {
            b = str.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lng += dlng;

        // GeoJSON coordinate order: [longitude, latitude]
        points.push([lng * 1e-5, lat * 1e-5]);
    }
    return points;
}
